import React, { useState, useCallback } from 'react'
import { DATA_TABLE_OPTIONS } from 'constants'
import {
  GridPagination,
  FilterBar as FilterBarBase,
  GridNoResultsOverlay,
} from 'components'
import { set } from 'date-fns'
/**
 * Handles all pagination and prop generation of data grid
 * @returns {[object, object]} - [dataGridProps, { page, pageSize }]
 * @description - dataGridProps - can be deestructured in mui datagrid component for common project _props for server pagination
 * { page, pageSize } - are exposed if needed for api query building
 */
export default function useServerPagedDataTable(_props) {
  const {
    isLoading = false,
    fixedParams = [],
    search = '',
    searchTramix = '',
    filterBarPresets = [],
    filterPresetParamName = 'paramState',
    customParamsInitialValues = {},
    rowIdKey = 'studentid',
    checkboxSelection = true,
    selectAllQuery = null, //this has to be a lazy query to be called when select all is clicked
    selectAllQueryParams = {},
    selectAllTransformResponse = null,
  } = _props

  const [query, setQuery] = useState({
    page: 1,
    params: {
      ...fixedParams,
      ...filterBarPresetsTemplate(),
      ...customParamsInitialValues,
    },
    limitNumber: DATA_TABLE_OPTIONS.rowPerPageDefault,
  })
  const [queryParamns, setQueryParamns] = useState({
    ...selectAllQueryParams,
  })
  function resetScroll() {
    let virtualScroller = document.querySelector('.MuiDataGrid-virtualScroller')
    if (virtualScroller) {
      document.querySelector('.MuiDataGrid-virtualScroller').scrollTop = 0
    }
  }

  function setPage(newPage) {
    setQuery({ ...query, page: newPage + 1 })
  }
  function setPageSize(newPageSize) {
    setQuery({ ...query, limitNumber: newPageSize })
  }

  const pageSize = query.limitNumber
  const page = query.page - 1

  const [selectedRows, setSelectedRows] = useState([])

  const [selectedRowIds, setSelectedRowIds] = useState([])

  /**
   * Ref holds data of currently visible rows on grid page
   * Used to assemble array of selected rows data
   */
  const currentPageRowsRef = React.useRef([])

  function setCurrentPageRows(rows) {
    currentPageRowsRef.current = rows
  }

  const onRowSelect = async (rows, details) => {
    try {
      if (details?.reason === 'multipleRowSelection' && selectAllQuery) {
        let newQueryParams = {
          ...selectAllQueryParams.params,
          search: search.trim().toUpperCase(),
          searchTramix: searchTramix.trim().toUpperCase(),
        }
        let newSearchAll = {
          params: newQueryParams,
          limitNumber: selectAllQueryParams.limitNumber,
          page: selectAllQueryParams.page,
        }
        if (selectedRowIds.length === 0) {
          let allSelectableRows = await selectAllQuery(newSearchAll).then(
            (response) => {
              let data = selectAllTransformResponse
                ? selectAllTransformResponse(response.data.rows)
                : response.data.rows
              setSelectedRows(data)
              setSelectedRowIds(data.map((item) => item[rowIdKey]))
              return data
            }
          )
        } else {
          setSelectedRows([])
          setSelectedRowIds([])
        }
      } else {
        let filteredArrayOfSelectedRows = selectedRows.filter((row) =>
          rows.includes(row[rowIdKey])
        )
        let missingRows = rows.filter((row) => !selectedRowIds.includes(row))
        setSelectedRows([
          ...filteredArrayOfSelectedRows,
          ...missingRows.map((id) =>
            currentPageRowsRef.current.find((item) => item[rowIdKey] == id)
          ),
        ])
        setSelectedRowIds(rows)
      }
    } catch (error) {}
  }

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      setQuery({ ...query, limitNumber: newPageSize, page: 1 })
    },
    [pageSize, query.params]
  )

  const PaginationComponent = React.useCallback(
    () => <GridPagination pageSize={pageSize} />,
    [pageSize]
  )

  /**
   * handle space between rows
   */
  const getRowSpacing = React.useCallback((_params) => {
    return {
      top: _params.isFirstVisible ? 0 : DATA_TABLE_OPTIONS.spaceBetweenRows,
      bottom: _params.isLastVisible ? 0 : DATA_TABLE_OPTIONS.spaceBetweenRows,
    }
  }, [])

  function filterBarPresetsTemplate() {
    if (filterBarPresets.length > 0) {
      return {
        [filterPresetParamName]: filterBarPresets[0]?.value,
      }
    }
    return {}
  }

  let updateCustomParams = (_object) => {
    let newParams = { ...query.params, ..._object }
    setQuery({ ...query, params: newParams, page: 1 })
    resetScroll()
  }

  /**
   * Handle changes in search input, update query params using a timeout reduce the number of api calls
   */
  React.useEffect(() => {
    let delaySearch = undefined
    if (!Boolean(searchTramix)) {
      if (Boolean(search)) {
        delaySearch = setTimeout(() => {
          let newParams = {
            ...query.params,
            search: search.trim().toUpperCase(),
          }
          let newQueryParams = {
            ...queryParamns.params,
            search: search.trim().toUpperCase(),
          }

          setQuery({ ...query, params: newParams })
          setQueryParamns({ ...queryParamns, params: newQueryParams })
        }, 800)
      } else {
        let { params } = query
        let newParams = { ...params, search: '' }
        setQuery({ ...query, params: newParams })
        setQueryParamns({ ...queryParamns, params: newParams })
        clearTimeout(delaySearch)
      }
    }
    return () => {
      clearTimeout(delaySearch)
    }
  }, [search])

  React.useEffect(() => {
    let delaySearch = undefined
    if (Boolean(searchTramix)) {
      delaySearch = setTimeout(() => {
        let newParams = {
          ...query.params,
          searchTramix: searchTramix.trim().toUpperCase(),
        }
        setQuery({ ...query, params: newParams })
      }, 800)
    } else {
      let { params } = query
      let newParams = { ...params, searchTramix: '' }
      setQuery({ ...query, params: newParams })
      clearTimeout(delaySearch)
    }
    return () => {
      clearTimeout(delaySearch)
    }
  }, [searchTramix])

  /**
   * Handle changes in custom filters, outside the filter bar radio buttons
   * @param {event} event - event object from radio button
   */
  const handleFilterChange = (event) => {
    let newParams = {
      ...query.params,
      [filterPresetParamName]: event.target.value,
    }
    setQuery({ ...query, params: newParams, page: 1 })
    resetScroll()
  }

  /**
   * Handles click on the column label in the table header, it toggles between 3 states - asc, desc and none
   * When none is selected, it will remove the sort param from the query
   * @param {array} sortModel - array of objects with sort params
   */
  function handleSort(sortModel) {
    let { sortBy = null, sortOrder = null, ...newParams } = query.params
    if (sortModel.length > 0) {
      newParams['sortBy'] = sortModel[0].field
      newParams['sortOrder'] = `${sortModel[0].sort}`.toUpperCase()
    }
    setQuery({
      ...query,
      params: newParams,
    })
  }

  /**
   * Handles changes in the selected radio option in the filter bar presets
   * @param {event} event - event object from radio button
   */
  const handleFilterBarChange = (event) => {
    let newParams = {
      ...query.params,
      [filterPresetParamName]: event.target.value,
    }
    setQuery({ ...query, params: newParams, page: 1 })
    resetScroll()
  }

  /**
   *
   * @param {*} _filterProps
   *
   * @returns {React.Component} - returns a react component for the filter bar along with radios for the filter presets
   */
  function FilterBarComponent(_filterProps) {
    if (filterBarPresets.length > 0) {
      return (
        <FilterBarBase
          presets={filterBarPresets}
          onChange={handleFilterBarChange}
          value={query.params[filterPresetParamName]}
        >
          {_filterProps.children}
        </FilterBarBase>
      )
    } else {
      return null
    }
  }

  const dataGridProps = {
    pageSize: pageSize,
    page: page,
    getRowSpacing: getRowSpacing,
    onPageChange: (newPage) => setPage(newPage),
    onPageSizeChange: handlePageSizeChange,
    paginationMode: 'server',
    sortingMode: 'server',
    components: {
      Pagination: PaginationComponent,
      NoRowsOverlay: GridNoResultsOverlay,
      NoResultsOverlay: GridNoResultsOverlay,
    },
    loading: isLoading,
    onSortModelChange: handleSort,
    //isRowSelectable:true,
    keepNonExistentRowsSelected: true,
    disableSelectionOnClick: true,
    checkboxSelection: checkboxSelection,
    onSelectionModelChange: (newSelectionModel, details) => {
      onRowSelect(newSelectionModel, details)
    },
    selectionModel: selectedRowIds,
  }

  return [
    dataGridProps,
    {
      FilterBarComponent,
      page,
      pageSize,
      query,
      handleFilterChange,
      handleSort,
      updateCustomParams,
      selectedRows,
      selectedRowIds,
      setCurrentPageRows,
    },
  ]
}
